import React, { createContext, useCallback, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { FluentProvider } from '@fluentui/react-components';
import { createCustomTheme } from './core/foundation';
import type { CustomTheme, ThemeMode } from './core/types';
import 'react-datepicker/dist/react-datepicker.css';
import './global.css';

export interface FluentThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  theme: CustomTheme;
}

export const FluentThemeContext = createContext<FluentThemeContextType | undefined>(undefined);

export interface FluentThemeProviderProps {
  children: ReactNode;
}

export const FluentThemeProvider: React.FC<FluentThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('app-theme-mode') as ThemeMode;
    return savedMode === 'light' || savedMode === 'dark' ? savedMode : 'light';
  });

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  }, []);

  const [theme, setTheme] = useState<CustomTheme>(() => createCustomTheme(mode));

  useEffect(() => {
    const newTheme = createCustomTheme(mode);
    setTheme(newTheme);

    localStorage.setItem('app-theme-mode', mode);

    const root = document.documentElement;
    const tokens = newTheme.designKitTokens[mode];

    document.body.style.backgroundColor = tokens.colorNeutralBackground1;

    const designTokens = newTheme.designKitTokens;

    root.style.setProperty('--fontFamilyBase', designTokens.fontFamilyBase);
    root.style.setProperty('--fontSizeBase400', designTokens.fontSizeBase400);
    root.style.setProperty('--fontWeightRegular', designTokens.fontWeightRegular);
    root.style.setProperty('--lineHeightBase400', designTokens.lineHeightBase400);

    root.style.setProperty('--fontSizeBase300', designTokens.fontSizeBase300);
    root.style.setProperty('--fontSizeBase500', designTokens.fontSizeBase500);
    root.style.setProperty('--fontWeightMedium', designTokens.fontWeightMedium);
    root.style.setProperty('--fontWeightSemibold', designTokens.fontWeightSemibold);
    root.style.setProperty('--lineHeightBase300', designTokens.lineHeightBase300);
    root.style.setProperty('--lineHeightBase500', designTokens.lineHeightBase500);

    root.style.setProperty('--borderRadiusSmall', designTokens.borderRadiusSmall);
    root.style.setProperty('--borderRadiusMedium', designTokens.borderRadiusMedium);
    root.style.setProperty('--borderRadiusLarge', designTokens.borderRadiusLarge);
    root.style.setProperty('--borderRadiusXLarge', designTokens.borderRadiusXLarge);
    root.style.setProperty('--borderRadiusXXXXLarge', designTokens.borderRadiusXXXXLarge);

    root.style.setProperty('--spacingVerticalS', designTokens.spacingVerticalS);
    root.style.setProperty('--spacingVerticalM', designTokens.spacingVerticalM);
    root.style.setProperty('--spacingVerticalL', designTokens.spacingVerticalL);
    root.style.setProperty('--spacingVerticalXL', designTokens.spacingVerticalXL);
    root.style.setProperty('--spacingVerticalXXL', designTokens.spacingVerticalXXL);

    root.style.setProperty('--spacingHorizontalS', designTokens.spacingHorizontalS);
    root.style.setProperty('--spacingHorizontalM', designTokens.spacingHorizontalM);
    root.style.setProperty('--spacingHorizontalL', designTokens.spacingHorizontalL);
    root.style.setProperty('--spacingHorizontalXL', designTokens.spacingHorizontalXL);
    root.style.setProperty('--spacingHorizontalXXL', designTokens.spacingHorizontalXXL);

    const scrollbarThumbColor = mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)';
    const scrollbarThumbHoverColor = mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)';
    const scrollbarThumbSubtleColor = mode === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)';
    const scrollbarThumbSubtleHoverColor = mode === 'light' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.25)';

    root.style.setProperty('--scrollbar-thumb-color', scrollbarThumbColor);
    root.style.setProperty('--scrollbar-thumb-hover-color', scrollbarThumbHoverColor);
    root.style.setProperty('--scrollbar-thumb-subtle-color', scrollbarThumbSubtleColor);
    root.style.setProperty('--scrollbar-thumb-subtle-hover-color', scrollbarThumbSubtleHoverColor);

    root.style.setProperty('--datepicker-bg-primary', tokens.colorNeutralBackground1);
    root.style.setProperty('--datepicker-bg-secondary', tokens.colorNeutralBackground2);
    root.style.setProperty('--datepicker-text-primary', tokens.colorNeutralForeground1);
    root.style.setProperty('--datepicker-text-secondary', tokens.colorNeutralForeground2);
    root.style.setProperty('--datepicker-text-disabled', tokens.colorNeutralForeground3);
    root.style.setProperty('--datepicker-border', tokens.colorNeutralBackground3);
    root.style.setProperty('--datepicker-brand-primary', tokens.colorBrand80);
    root.style.setProperty('--datepicker-brand-primary-hover', tokens.colorBrand90);
    root.style.setProperty('--datepicker-brand-light', tokens.colorGreenBackground);
    root.style.setProperty('--datepicker-brand-light-color', tokens.colorNeutralForeground1);
    root.style.setProperty('--datepicker-border-radius', designTokens.borderRadiusXXXLarge);
    root.style.setProperty('--datepicker-box-shadow', designTokens.shadow16);

    root.style.setProperty('--spinner-track-color', tokens.spinnerTrackColor);
    root.style.setProperty('--spinner-tail-color', tokens.spinnerTailColor);
  }, [mode]);

  const contextValue = useMemo(() => ({ mode, toggleTheme, theme }), [mode, toggleTheme, theme]);

  return (
    <FluentThemeContext.Provider value={contextValue}>
      <FluentProvider theme={theme}>{children}</FluentProvider>
    </FluentThemeContext.Provider>
  );
};

/**
 * Hook to get current theme tokens - cleaner API
 * Returns both the current mode tokens and all tokens
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useFluentTokens = () => {
  const context = useContext(FluentThemeContext);
  if (!context) {
    throw new Error('useFluentTokens must be used within a FluentThemeProvider');
  }

  const { mode, toggleTheme } = context;
  const [theme, setTheme] = useState<CustomTheme>(() => createCustomTheme(mode));

  useEffect(() => {
    setTheme(createCustomTheme(mode));
  }, [mode]);

  return {
    mode,
    toggleTheme,
    tokens: theme.designKitTokens,
    modeTokens: theme.designKitTokens[mode],
  };
};
